import {
  WEBHOOK_ID_PATTERN,
  WEBHOOK_MAX_ATTEMPTS,
  WEBHOOK_MAX_TIMEOUT_MS,
  WEBHOOK_METHODS,
  WEBHOOK_TEMPLATE_VARIABLES,
  type WebhookDelivery,
  type WebhookDeliveryStatus,
  type WebhookMethod,
  type WebhookTestResult,
} from '@kkp-fraud/shared';

/**
 * Webhook connections' pure half (R3 — "call an API like Postman"): the wire shapes of the admin
 * service's `/api/webhooks`, and what the editor checks before it sends. The admin service
 * decides (`dotnet/admin-api` `WebhookInput` — including the SSRF check, which needs DNS); these
 * mirror its simple rules so an operator sees a problem before a round trip, not instead of one.
 *
 * Secrets are WRITE-ONLY: the service never returns a secret header's value or an auth field,
 * only that one is set (`valueSet`, `tokenSet`…). The editor therefore sends a secret only when
 * the operator typed a new one; leaving the box empty keeps the stored value.
 */

export { WEBHOOK_METHODS, WEBHOOK_TEMPLATE_VARIABLES };
export const WEBHOOK_AUTH_TYPES = ['none', 'bearer', 'basic', 'header'] as const;
export type WebhookAuthType = (typeof WEBHOOK_AUTH_TYPES)[number];
export const WEBHOOK_DELIVERY_STATUSES: readonly WebhookDeliveryStatus[] = ['sent', 'failed', 'retrying'];
export const WEBHOOK_MIN_TIMEOUT_MS = 100;
export const WEBHOOK_MAX_BACKOFF_MS = 60_000;
/** Header names the dispatcher sets itself (`WebhookInput.ReservedHeaders`). */
export const RESERVED_HEADERS = ['host', 'content-length', 'transfer-encoding', 'connection', 'keep-alive', 'upgrade', 'te', 'trailer', 'content-type', 'x-kkp-delivery-id', 'x-kkp-alert-rule'];

export const WEBHOOK_ROUTES = {
  webhooks: '/api/webhooks',
  preview: '/api/webhooks/preview',
} as const;

export const webhookPath = (id: string, suffix = ''): string => `${WEBHOOK_ROUTES.webhooks}/${encodeURIComponent(id)}${suffix}`;

/** `GET /api/webhooks` row. */
export interface WebhookView {
  id: string;
  name: string;
  method: WebhookMethod;
  url: string;
  headers: { name: string; value?: string; secret: boolean; valueSet?: boolean }[];
  auth: { type: WebhookAuthType; name?: string; tokenSet?: boolean; userSet?: boolean; passwordSet?: boolean; valueSet?: boolean };
  bodyTemplate: string;
  contentType: string;
  timeoutMs: number;
  retry: { attempts: number; backoffMs: number };
  enabled: boolean;
  createdUtc: string;
  createdBy: string;
  updatedUtc: string;
  updatedBy: string;
  /** Sent back as `If-Match` on PUT / DELETE. */
  etag: string;
  /** QA-A-19: true when the admin service shortened `url` for a read-only caller (scheme and host only). */
  urlRedacted?: boolean;
}

/** The body of POST / PUT. A secret that is absent keeps its stored value on a PUT. */
export interface WebhookWrite {
  id?: string;
  name: string;
  method: WebhookMethod;
  url: string;
  headers: { name: string; value?: string; secret: boolean }[];
  auth: { type: WebhookAuthType; token?: string; user?: string; password?: string; name?: string; value?: string };
  bodyTemplate: string;
  contentType: string;
  timeoutMs: number;
  retry: { attempts: number; backoffMs: number };
  enabled: boolean;
}

export interface WebhookPreview {
  body: string;
  unknown: string[];
  /** null when the content type is not JSON or the body is empty. */
  jsonValid: boolean | null;
}

export interface WebhookDeliveryPage {
  webhookId: string;
  source: 'cosmos' | 'dispatcher' | 'none';
  items: WebhookDelivery[];
  nextCursor?: string;
}

export interface DeliveryFilters {
  status?: WebhookDeliveryStatus;
  /** true = "Send test" only, false = alerts only. */
  test?: boolean;
}

export type { WebhookDelivery, WebhookTestResult };

// ---- the editor's form ---------------------------------------------------------------------

export interface HeaderRow {
  name: string;
  /** Typed by the operator. For a stored secret, empty = keep. */
  value: string;
  secret: boolean;
  /** A secret value is stored (from the server) — "•••• set". */
  stored: boolean;
}

export interface WebhookForm {
  id: string;
  name: string;
  method: WebhookMethod;
  url: string;
  headers: HeaderRow[];
  authType: WebhookAuthType;
  /** Typed secrets; empty = keep what is stored. */
  token: string;
  user: string;
  password: string;
  authHeaderName: string;
  authHeaderValue: string;
  /** Which auth secrets the server holds for the SAVED type. */
  storedAuth: { type: WebhookAuthType; token: boolean; user: boolean; password: boolean; value: boolean };
  bodyTemplate: string;
  contentType: string;
  timeoutMs: number;
  attempts: number;
  backoffMs: number;
  enabled: boolean;
}

export const DEFAULT_BODY_TEMPLATE = `{
  "transactionId": "{{transactionId}}",
  "decision": "{{decision}}",
  "card": "{{cardId}}",
  "amountThb": {{amountThb}},
  "rules": {{ruleIds}},
  "alertRule": "{{alertRuleId}}"
}`;

export function emptyForm(): WebhookForm {
  return {
    id: '',
    name: '',
    method: 'POST',
    url: 'https://',
    headers: [],
    authType: 'none',
    token: '',
    user: '',
    password: '',
    authHeaderName: '',
    authHeaderValue: '',
    storedAuth: { type: 'none', token: false, user: false, password: false, value: false },
    bodyTemplate: DEFAULT_BODY_TEMPLATE,
    contentType: 'application/json',
    timeoutMs: 5000,
    attempts: 3,
    backoffMs: 1000,
    enabled: true,
  };
}

export function formOf(w: WebhookView): WebhookForm {
  return {
    id: w.id,
    name: w.name,
    method: w.method,
    url: w.url,
    headers: w.headers.map((h) => ({ name: h.name, value: h.secret ? '' : (h.value ?? ''), secret: h.secret, stored: h.secret && h.valueSet === true })),
    authType: w.auth.type,
    token: '',
    user: '',
    password: '',
    authHeaderName: w.auth.name ?? '',
    authHeaderValue: '',
    storedAuth: {
      type: w.auth.type,
      token: w.auth.tokenSet === true,
      user: w.auth.userSet === true,
      password: w.auth.passwordSet === true,
      value: w.auth.valueSet === true,
    },
    bodyTemplate: w.bodyTemplate,
    contentType: w.contentType,
    timeoutMs: w.timeoutMs,
    attempts: w.retry.attempts,
    backoffMs: w.retry.backoffMs,
    enabled: w.enabled,
  };
}

/** The request body: typed secrets only (an empty box keeps the stored one). */
export function writeOf(f: WebhookForm, isNew: boolean): WebhookWrite {
  const secret = (typed: string) => (typed !== '' ? typed : undefined);
  const auth: WebhookWrite['auth'] =
    f.authType === 'bearer'
      ? { type: 'bearer', ...(secret(f.token) ? { token: f.token } : {}) }
      : f.authType === 'basic'
        ? { type: 'basic', ...(secret(f.user) ? { user: f.user } : {}), ...(secret(f.password) ? { password: f.password } : {}) }
        : f.authType === 'header'
          ? { type: 'header', name: f.authHeaderName.trim(), ...(secret(f.authHeaderValue) ? { value: f.authHeaderValue } : {}) }
          : { type: 'none' };
  return {
    ...(isNew ? { id: f.id.trim() } : {}),
    name: f.name.trim(),
    method: f.method,
    url: f.url.trim(),
    headers: f.headers
      .filter((h) => h.name.trim() !== '' || h.value !== '')
      .map((h) => (h.secret ? { name: h.name.trim(), secret: true, ...(h.value !== '' ? { value: h.value } : {}) } : { name: h.name.trim(), value: h.value, secret: false })),
    auth,
    bodyTemplate: f.bodyTemplate,
    contentType: f.contentType.trim(),
    timeoutMs: f.timeoutMs,
    retry: { attempts: f.attempts, backoffMs: f.backoffMs },
    enabled: f.enabled,
  };
}

// ---- checks the editor makes before sending --------------------------------------------------

export type WebhookProblem =
  | 'id'
  | 'name'
  | 'url'
  | 'header_name'
  | 'header_reserved'
  | 'header_duplicate'
  | 'header_secret_missing'
  | 'auth_secret_missing'
  | 'auth_header_name'
  | 'content_type'
  | 'timeout'
  | 'attempts'
  | 'backoff'
  | 'unknown_variable';

/** Literal keys, so the catalogue's dead-key gate sees every one. */
export const WEBHOOK_PROBLEM_KEY: Record<WebhookProblem, string> = {
  id: 'webhooks.problem.id',
  name: 'webhooks.problem.name',
  url: 'webhooks.problem.url',
  header_name: 'webhooks.problem.header_name',
  header_reserved: 'webhooks.problem.header_reserved',
  header_duplicate: 'webhooks.problem.header_duplicate',
  header_secret_missing: 'webhooks.problem.header_secret_missing',
  auth_secret_missing: 'webhooks.problem.auth_secret_missing',
  auth_header_name: 'webhooks.problem.auth_header_name',
  content_type: 'webhooks.problem.content_type',
  timeout: 'webhooks.problem.timeout',
  attempts: 'webhooks.problem.attempts',
  backoff: 'webhooks.problem.backoff',
  unknown_variable: 'webhooks.problem.unknown_variable',
};

const HEADER_NAME = /^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/;
const CONTENT_TYPE = /^[A-Za-z0-9!#$&^_.+-]+\/[A-Za-z0-9!#$&^_.+-]+(\s*;[^\r\n]*)?$/;
const PLACEHOLDER = /\{\{\s*([^{}\s]*)\s*\}\}/g;
const KNOWN = new Set<string>(WEBHOOK_TEMPLATE_VARIABLES);

/** Placeholders outside the variable set, once each, in order. */
export function unknownVariables(template: string): string[] {
  const out: string[] = [];
  for (const m of template.matchAll(PLACEHOLDER)) {
    const name = m[1]!;
    if (!KNOWN.has(name) && !out.includes(name)) out.push(name);
  }
  return out;
}

const whole = (n: number, min: number, max: number) => Number.isInteger(n) && n >= min && n <= max;

/** https — or http, which the service accepts only for its allow-listed hosts; it has the final word. */
function urlLooksRight(raw: string): boolean {
  try {
    const u = new URL(raw.trim());
    return (u.protocol === 'https:' || u.protocol === 'http:') && u.hostname !== '' && u.username === '' && u.password === '';
  } catch {
    return false;
  }
}

export function webhookProblems(f: WebhookForm, isNew: boolean): WebhookProblem[] {
  const out: WebhookProblem[] = [];
  if (isNew && !WEBHOOK_ID_PATTERN.test(f.id.trim())) out.push('id');
  if (f.name.trim() === '' || f.name.trim().length > 100) out.push('name');
  if (!urlLooksRight(f.url)) out.push('url');
  const seen = new Set<string>();
  for (const h of f.headers) {
    const name = h.name.trim();
    if (name === '' && h.value === '') continue;
    if (!HEADER_NAME.test(name)) out.push('header_name');
    else if (RESERVED_HEADERS.includes(name.toLowerCase())) out.push('header_reserved');
    else if (seen.has(name.toLowerCase())) out.push('header_duplicate');
    seen.add(name.toLowerCase());
    if (h.secret && !h.stored && h.value === '') out.push('header_secret_missing');
  }
  const kept = f.storedAuth.type === f.authType ? f.storedAuth : { token: false, user: false, password: false, value: false };
  if (f.authType === 'bearer' && f.token === '' && !kept.token) out.push('auth_secret_missing');
  if (f.authType === 'basic' && ((f.user === '' && !kept.user) || (f.password === '' && !kept.password))) out.push('auth_secret_missing');
  if (f.authType === 'header') {
    const n = f.authHeaderName.trim();
    if (!HEADER_NAME.test(n) || RESERVED_HEADERS.includes(n.toLowerCase())) out.push('auth_header_name');
    if (f.authHeaderValue === '' && !kept.value) out.push('auth_secret_missing');
  }
  if (!CONTENT_TYPE.test(f.contentType.trim())) out.push('content_type');
  if (!whole(f.timeoutMs, WEBHOOK_MIN_TIMEOUT_MS, WEBHOOK_MAX_TIMEOUT_MS)) out.push('timeout');
  if (!whole(f.attempts, 1, WEBHOOK_MAX_ATTEMPTS)) out.push('attempts');
  if (!whole(f.backoffMs, 0, WEBHOOK_MAX_BACKOFF_MS)) out.push('backoff');
  if (unknownVariables(f.bodyTemplate).length > 0) out.push('unknown_variable');
  return [...new Set(out)];
}

/** The URL as a list shows it: host and path, never the query (it may carry a token). */
export function shortUrl(url: string): string {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname === '/' ? '' : u.pathname}`;
  } catch {
    return url;
  }
}

/** The admin service's refusals, worded by the catalogue; null = show the message it sent. */
export function webhookErrorKey(code: string | undefined): string | null {
  switch (code) {
    case 'webhook_exists':
      return 'webhooks.error.exists';
    case 'etag_mismatch':
      return 'webhooks.error.etag';
    case 'webhook_in_use':
      return 'webhooks.error.in_use';
    case 'webhook_not_found':
      return 'webhooks.error.not_found';
    case 'rules_unavailable':
    case 'webhook_store_unavailable':
      return 'webhooks.error.unavailable';
    case 'alerts_not_configured':
    case 'dispatcher_unreachable':
    case 'dispatcher_auth':
      return 'webhooks.error.dispatcher';
    case 'rate_limited':
      return 'webhooks.error.rate_limited';
    default:
      return null;
  }
}
